import React from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Card, CardContent, CardHeader, Chip, Stack, Tooltip, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import type { Book } from '../../books/types';
import { buildHistogramData, formatShortDate } from '../../books/utils';

interface BooksHistogramProps {
  books: Book[];
}

const BooksHistogram: React.FC<BooksHistogramProps> = ({ books }) => {
  const histogram = React.useMemo(() => buildHistogramData(books), [books]);

  return (
    <Card>
      <CardHeader
        avatar={<BarChartIcon />}
        title="Pages read per day"
        subheader="Last 21 days, stacked by book"
        sx={{ pb: 0 }}
      />
      <CardContent>
        {histogram.maxTotalPages === 0 ? (
          <Typography color="text.secondary">No reading history yet. Move a book forward to start the histogram.</Typography>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${histogram.days.length}, minmax(0, 1fr))`,
                gap: 1,
                alignItems: 'end',
                minHeight: 220,
              }}
            >
              {histogram.days.map((day) => (
                <Tooltip
                  key={day.isoDate}
                  title={
                    day.totalPages > 0
                      ? `${formatShortDate(day.isoDate)}: ${day.contributions
                          .map((entry) => `${entry.title} ${entry.pages}`)
                          .join(' • ')}`
                      : `${formatShortDate(day.isoDate)}: no pages read`
                  }
                  arrow
                >
                  <Stack spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: '100%',
                        minWidth: 10,
                        height: 180,
                        borderRadius: 999,
                        bgcolor: 'action.disabledBackground',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        overflow: 'hidden',
                      }}
                    >
                      {day.contributions.map((entry) => (
                        <Box
                          key={entry.bookId}
                          sx={{
                            width: '100%',
                            height: `${(entry.pages / histogram.maxTotalPages) * 180}px`,
                            minHeight: entry.pages > 0 ? 6 : 0,
                            bgcolor: entry.color,
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {day.label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {day.totalPages > 0 ? day.totalPages : ''}
                    </Typography>
                  </Stack>
                </Tooltip>
              ))}
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {histogram.legend.map((entry) => (
                <Chip
                  key={entry.bookId}
                  label={`${entry.title} · ${entry.totalPages}`}
                  sx={{
                    '& .MuiChip-label': { maxWidth: 220 },
                    bgcolor: alpha(entry.color, 0.14),
                    borderColor: entry.color,
                    color: 'text.primary',
                  }}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default BooksHistogram;
